'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { StructuredData } from './SEO';

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbProps {
  items?: BreadcrumbItem[];
  className?: string;
}

/**
 * 面包屑导航组件
 * 如果没有提供 items，会自动根据当前路径生成
 */
export default function Breadcrumb({ items, className = '' }: BreadcrumbProps) {
  const pathname = usePathname();
  const [isClient, setIsClient] = React.useState(false);

  // 确保只在客户端执行
  React.useEffect(() => {
    setIsClient(true);
  }, []);

  // 如果没有提供 items，根据路径自动生成
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    if (items) return items;

    // 服务端渲染时返回默认面包屑
    if (!isClient || !pathname) {
      return [{ name: 'Home', url: '/' }];
    }

    const paths = pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [
      { name: 'Home', url: '/' },
    ];

    let currentPath = '';
    paths.forEach((path, index) => {
      currentPath += `/${path}`;
      // 将路径转换为英文名称
      const nameMap: Record<string, string> = {
        'about-us': 'About Us',
        'products': 'Products',
        'news': 'News',
        'case': 'Case Center',
        'download': 'Downloads',
        'video': 'Videos',
        'contact-us': 'Contact Us',
      };

      const name = nameMap[path] || path
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      breadcrumbs.push({
        name,
        url: currentPath,
      });
    });

    return breadcrumbs;
  };

  const breadcrumbItems = generateBreadcrumbs();

  if (breadcrumbItems.length <= 1) return null;

  return (
    <>
      <nav
        aria-label="Breadcrumb Navigation"
        className={`breadcrumb ${className}`}
        style={{
          padding: '12px 0',
          fontSize: '14px',
          color: '#7A7A7A',
        }}
      >
        <ol
          className="flex items-center flex-wrap gap-2"
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
          }}
        >
          {breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1;

            return (
              <li key={item.url} className="flex items-center">
                {index > 0 && (
                  <span
                    className="mx-2"
                    style={{ color: '#ccc' }}
                    aria-hidden="true"
                  >
                    /
                  </span>
                )}
                {isLast ? (
                  <span
                    className="text-gray-700 font-medium"
                    aria-current="page"
                  >
                    {item.name}
                  </span>
                ) : (
                  <Link
                    href={item.url}
                    className="hover:text-primary transition-colors"
                  >
                    {item.name}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
      <StructuredData
        type="BreadcrumbList"
        data={{ items: breadcrumbItems }}
      />
    </>
  );
}

